import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "../../components/Common/AppOverlay";
import * as Yup from "yup";
import { useFormik } from "formik";
import { SuccessNotify, ErrorNotify } from "../../shared/Alert";
import { showLoading } from "react-global-loading";
import {
  createFinancialYear,
  updateFinancialYear,
  getFinancialYearDetail,
} from "../../helpers/backend_helper";

const emptyFinancialYear = {
  id: 0,
  name: "",
  startDate: "",
  endDate: "",
  description: "",
};

const dateInputValue = (value) => (value ? String(value).slice(0, 10) : "");

const FinancialYearDetail = ({ detailId = 0, closeAction }) => {
  const [formData, setFormData] = useState(emptyFinancialYear);

  useEffect(() => {
    if (!detailId) {
      setFormData(emptyFinancialYear);
      return;
    }

    showLoading(true);
    getFinancialYearDetail(detailId)
      .then((response) => {
        if (!response?.status) {
          ErrorNotify(
            response?.message || "Unable to load the financial year."
          );
          return;
        }
        setFormData({
          id: response.data.id,
          name: response.data.name || "",
          startDate: dateInputValue(response.data.startDate),
          endDate: dateInputValue(response.data.endDate),
          description: response.data.description || "",
        });
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load the financial year.");
      })
      .finally(() => showLoading(false));
  }, [detailId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: formData,
    validationSchema: Yup.object({
      name: Yup.string().trim().required("Enter a financial year name"),
      startDate: Yup.string().required("Select a start date"),
      endDate: Yup.string()
        .required("Select an end date")
        .test(
          "after-start",
          "End date must be on or after the start date",
          function (value) {
            return (
              !value || !this.parent.startDate || value >= this.parent.startDate
            );
          }
        ),
      description: Yup.string().max(
        500,
        "Description cannot exceed 500 characters"
      ),
    }),
    onSubmit: async (values) => {
      showLoading(true);
      const request = {
        name: values.name.trim(),
        startDate: values.startDate,
        endDate: values.endDate,
        description: values.description?.trim() || "",
        ...(values.id ? { id: values.id } : {}),
      };

      try {
        const response = values.id
          ? await updateFinancialYear(request)
          : await createFinancialYear(request);
        if (!response?.status) {
          ErrorNotify(
            response?.message || "Unable to save the financial year."
          );
          return;
        }
        SuccessNotify(
          `Financial year ${values.id ? "updated" : "created"} successfully.`
        );
        closeAction?.();
      } catch (error) {
        console.error(error);
        ErrorNotify("Unable to save the financial year.");
      } finally {
        showLoading(false);
      }
    },
  });

  const isEditing = Boolean(validation.values.id);

  return (
    <Form
      className="standard-drawer-form financial-year-drawer-form"
      onSubmit={(event) => {
        event.preventDefault();
        validation.handleSubmit();
      }}
    >
      <div className="standard-drawer-form__content">
        <p className="standard-drawer-form__intro">
          {isEditing
            ? "Update this reporting period and its date range."
            : "Add a reporting period for use across engagements and inspections."}
        </p>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-calendar" />
              Financial Year Information
            </span>
            <i className="bx bx-chevron-down" />
          </div>
          <FormGroup>
            <Label htmlFor="financial-year-name">
              Financial Year <b>*</b>
            </Label>
            <Input
              id="financial-year-name"
              name="name"
              placeholder="Enter financial year name"
              value={validation.values.name}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.name && validation.errors.name
              )}
            />
            {validation.touched.name && validation.errors.name && (
              <FormFeedback>{validation.errors.name}</FormFeedback>
            )}
          </FormGroup>
          <div className="financial-year-drawer-form__dates">
            <FormGroup>
              <Label htmlFor="financial-year-start-date">
                Start Date <b>*</b>
              </Label>
              <Input
                id="financial-year-start-date"
                name="startDate"
                type="date"
                value={validation.values.startDate}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={Boolean(
                  validation.touched.startDate && validation.errors.startDate
                )}
              />
              {validation.touched.startDate && validation.errors.startDate && (
                <FormFeedback>{validation.errors.startDate}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <Label htmlFor="financial-year-end-date">
                End Date <b>*</b>
              </Label>
              <Input
                id="financial-year-end-date"
                name="endDate"
                type="date"
                min={validation.values.startDate || undefined}
                value={validation.values.endDate}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={Boolean(
                  validation.touched.endDate && validation.errors.endDate
                )}
              />
              {validation.touched.endDate && validation.errors.endDate && (
                <FormFeedback>{validation.errors.endDate}</FormFeedback>
              )}
            </FormGroup>
          </div>
          <FormGroup>
            <Label htmlFor="financial-year-description">Description</Label>
            <Input
              id="financial-year-description"
              name="description"
              type="textarea"
              rows="5"
              maxLength="500"
              placeholder="Describe this financial year"
              value={validation.values.description}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.description && validation.errors.description
              )}
            />
            {validation.touched.description &&
              validation.errors.description && (
                <FormFeedback>{validation.errors.description}</FormFeedback>
              )}
            <div className="standard-drawer-form__count">
              {validation.values.description.length} / 500
            </div>
          </FormGroup>
        </section>
      </div>
      <footer className="standard-drawer-form__actions">
        <Button
          className="standard-drawer-form__cancel"
          type="button"
          onClick={closeAction}
        >
          Cancel
        </Button>
        <Button className="standard-drawer-form__submit" type="submit">
          {isEditing ? "Save Changes" : "Create Financial Year"}
        </Button>
      </footer>
    </Form>
  );
};

export default FinancialYearDetail;
